import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import {
  Calendar,
  Users,
  Clock,
  Search,
  BookOpen,
  Settings,
  MapPin,
  FileText,
  PlusCircle,
  MessageSquare,
} from 'lucide-react';
import { Button } from '../components/Button';
import { Header } from '../components/Header';
import { StatsCard } from '../components/StatsCard';
import { QuickActionCard } from '../components/QuickActionCard';
import { AccountSidebar } from '../components/AccountSidebar';
import { Badge } from '../components/Badge';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { DeleteAccountModal } from '../components/DeleteAccountModal';
import { DeletionSuccessModal } from '../components/DeletionSuccessModal';
import { CreateUserProfileModal } from '../components/CreateUserProfileModal';
import { useAuth } from '../context/AuthContext';
import { createUserProfile, getUpcomingBookings } from '../services/api';
import { UserProfileFormData, Booking } from '../types';
import { ROUTES } from '../constants';

export const ServiceUserHomePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, deleteAccount, logout, updateUserProfileStatus } = useAuth();

  if (user?.role === 'admin') {
    return <Navigate to={ROUTES.ADMIN_DASHBOARD} replace />;
  }
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeletionSuccessModal, setShowDeletionSuccessModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [upcomingBookings, setUpcomingBookings] = useState<Booking[]>([]);
  const [upcomingCount, setUpcomingCount] = useState(0);
  const [bookingsLoading, setBookingsLoading] = useState(false);

  const isProfileIncomplete = !user?.isProfileCompleted;

  useEffect(() => {
    if (user && user.role !== 'admin' && !isProfileIncomplete) {
      fetchUpcoming();
    }
  }, [user, isProfileIncomplete]);

  const fetchUpcoming = async () => {
    setBookingsLoading(true);
    try {
      const response = await getUpcomingBookings();
      if (response.success) {
        setUpcomingBookings(response.bookings);
        setUpcomingCount(response.count);
      }
    } catch (err) {
      console.error('Failed to fetch upcoming bookings:', err);
    } finally {
      setBookingsLoading(false);
    }
  };

  const handleDeleteAccount = async (reason: string) => {
    const result = await deleteAccount(reason);

    if (result.success) {
      setShowDeleteModal(false);
      setTimeout(() => setShowDeletionSuccessModal(true), 0);
    }

    return result;
  };

  const handleCreateProfile = async (formData: UserProfileFormData) => {
    try {
      const result = await createUserProfile(formData);

      if (result.success) {
        updateUserProfileStatus(true, true);
        return { success: true };
      } else {
        return { success: false, error: result.error || 'Failed to create profile' };
      }
    } catch (error) {
      return { success: false, error: 'An unexpected error occurred' };
    }
  };

  const handleProfileSuccess = () => {
    setShowProfileModal(false);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      <Header
        theme="blue"
        showNotifications={true}
        showProfileAlert={isProfileIncomplete}
        onProfileAlertClick={() => setShowProfileModal(true)}
      />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {isProfileIncomplete && (
          <div className="mb-6 bg-blue-50 border-l-4 border-blue-600 p-4 rounded-r-lg">
            <div className="flex items-start gap-3">
              <FileText className="text-blue-600 mt-0.5" size={20} />
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900 mb-1">Complete Your Profile</h3>
                <p className="text-sm text-blue-800 mb-3">
                  Please complete your profile to access all features and book care services. This helps providers understand your care needs better.
                </p>
                <Button
                  onClick={() => setShowProfileModal(true)}
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  Complete Profile Now
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Welcome back, {user?.name || 'User'}
          </h1>
          <p className="text-gray-600">
            Manage your care services and bookings from your dashboard
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <StatsCard
            title="Upcoming Bookings"
            value={upcomingCount}
            icon={Calendar}
            theme="blue"
            trend={upcomingCount > 0 ? { value: `${upcomingCount} scheduled`, isPositive: true } : undefined}
          />
          <StatsCard
            title="Active Providers"
            value={5}
            icon={Users}
            theme="blue"
          />
          <StatsCard
            title="Care Hours This Month"
            value={24}
            icon={Clock}
            theme="blue"
            trend={{ value: '+12%', isPositive: true }}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <QuickActionCard
                  title="Find Care Providers"
                  description="Search care providers near your location"
                  icon={Search}
                  theme="blue"
                  onClick={() => isProfileIncomplete ? setShowProfileModal(true) : navigate(ROUTES.FIND_PROVIDERS)}
                />
                <QuickActionCard
                  title="My Bookings"
                  description="View and manage your bookings"
                  icon={BookOpen}
                  theme="blue"
                  onClick={() => isProfileIncomplete ? setShowProfileModal(true) : navigate(ROUTES.MY_BOOKINGS)}
                />
                <QuickActionCard
                  title="My Care Posts"
                  description="Create and manage care request posts"
                  icon={PlusCircle}
                  theme="blue"
                  onClick={() => isProfileIncomplete ? setShowProfileModal(true) : navigate(ROUTES.MY_POSTS)}
                />
                <QuickActionCard
                  title="Messages"
                  description="View messages from care providers"
                  icon={MessageSquare}
                  theme="blue"
                  onClick={() => navigate(ROUTES.MESSAGES)}
                />
                <QuickActionCard
                  title="Settings"
                  description="Update your profile and preferences"
                  icon={Settings}
                  theme="blue"
                  onClick={() => navigate(ROUTES.SETTINGS)}
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">Upcoming Appointments</h2>
                {upcomingCount > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(ROUTES.MY_BOOKINGS)}
                  >
                    View All
                  </Button>
                )}
              </div>
              {bookingsLoading ? (
                <div className="flex justify-center py-12 bg-white rounded-xl border border-gray-200">
                  <LoadingSpinner size="md" />
                </div>
              ) : upcomingBookings.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
                  <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-600 font-medium mb-1">No upcoming appointments</p>
                  <p className="text-sm text-gray-500 mb-4">
                    Book a care provider to get started
                  </p>
                  <Button
                    variant="primary"
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700"
                    onClick={() => navigate(ROUTES.FIND_PROVIDERS)}
                  >
                    Find Providers
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {upcomingBookings.map((booking) => (
                    <div
                      key={booking._id}
                      className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-md transition-shadow"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h3 className="text-lg font-semibold text-gray-900">
                                {booking.patientSnapshot?.recipientName
                                  ? `Care for ${booking.patientSnapshot.recipientName}`
                                  : `Booking #${booking.bookingId}`}
                              </h3>
                              <p className="text-sm text-gray-500">Booking #{booking.bookingId}</p>
                            </div>
                            <Badge
                              variant={
                                booking.status === 'Accepted' ? 'success' :
                                booking.status === 'Pending' ? 'warning' :
                                booking.status === 'Cancelled' || booking.status === 'Rejected' ? 'error' :
                                'info'
                              }
                            >
                              {booking.status}
                            </Badge>
                          </div>
                          <p className="text-gray-600 font-medium mb-3">{booking.serviceType}</p>
                          <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                            <div className="flex items-center gap-2">
                              <Calendar className="h-4 w-4" />
                              {formatDate(booking.appointmentDate)}
                            </div>
                            <div className="flex items-center gap-2">
                              <Clock className="h-4 w-4" />
                              {new Date(booking.appointmentDate).toLocaleTimeString('en-GB', {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </div>
                            {booking.patientSnapshot?.city && (
                              <div className="flex items-center gap-2">
                                <MapPin className="h-4 w-4" />
                                {booking.patientSnapshot.city}
                              </div>
                            )}
                          </div>
                          {booking.notes && (
                            <p className="text-sm text-gray-500 mt-2 italic">"{booking.notes}"</p>
                          )}
                        </div>
                        <div className="flex sm:flex-col gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1 sm:flex-none"
                            onClick={() => navigate(ROUTES.MY_BOOKINGS)}
                          >
                            View Details
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-6">
            <AccountSidebar
              name={user?.name || 'User'}
              email={user?.email || 'user@example.com'}
              accountType="Service User"
              memberSince={new Date(user?.createdAt || Date.now()).toLocaleDateString('en-GB', {
                month: 'short',
                year: 'numeric',
              })}
              theme="blue"
              verified={true}
              onDeleteAccount={() => setShowDeleteModal(true)}
              userRole="user"
            />

            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Need Help?</h3>
              <p className="text-sm text-gray-600 mb-4">
                Our support team is here to assist you with any questions or concerns.
              </p>
              <Button variant="outline" className="w-full">
                Contact Support
              </Button>
            </div>
          </div>
        </div>
      </main>

      <DeleteAccountModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirmDelete={handleDeleteAccount}
        userRole="service_user"
      />

      <DeletionSuccessModal
        isOpen={showDeletionSuccessModal}
        onClose={async () => {
          setShowDeletionSuccessModal(false);
          await logout();
        }}
      />

      <CreateUserProfileModal
        isOpen={isProfileIncomplete || showProfileModal}
        userId={user?.userId || ''}
        onSuccess={handleProfileSuccess}
        onSubmit={handleCreateProfile}
      />
    </div>
  );
};
